import React, { useEffect, useMemo, useState } from "react";
import { Users, Plus, Trash2, Pencil, ClipboardList, BarChart3, Search, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import GroupFormModal from "./GroupFormModal";
import GroupDetailDrawer from "./GroupDetailDrawer";

/**
 * Teacher groups tab — create / edit / delete groups of students, assign
 * quizzes / homework / flashcard reviews, and drill down into group
 * analytics (avg scores per chapter + leaderboard).
 */
export default function GroupsTab() {
  const [groups, setGroups] = useState([]);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | group object
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [gs, rs] = await Promise.all([
        api.get("/staff/groups"),
        api.get("/staff/roster"),
      ]);
      setGroups(gs.data);
      setRoster(rs.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    if (!t) return groups;
    return groups.filter((g) => (g.name || "").toLowerCase().includes(t));
  }, [groups, q]);

  const handleDelete = async (group) => {
    if (!window.confirm(`Delete group "${group.name}"? This removes the group and all its assignments. Student progress is untouched.`)) return;
    await api.delete(`/staff/groups/${group.group_id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="groups-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search groups by name…"
            data-testid="groups-search"
            className="w-full ps-10 pe-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage"
          />
        </div>
        <button
          onClick={() => setEditing("new")}
          data-testid="groups-new-btn"
          className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-white font-medium px-4 py-2.5 rounded-xl transition-all"
        >
          <Plus className="w-4 h-4" /> New group
        </button>
      </div>

      {loading && (
        <div className="bg-white border border-border rounded-2xl p-8 text-center text-muted-foreground">
          Loading groups…
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="bg-white border border-dashed border-border rounded-2xl p-10 text-center" data-testid="groups-empty-state">
          <div className="mx-auto w-12 h-12 rounded-xl bg-sage/10 text-sage flex items-center justify-center mb-3">
            <Users className="w-6 h-6" strokeWidth={1.7} />
          </div>
          <h3 className="font-display text-lg font-semibold mb-1">
            {q ? "No groups match your search" : "Create your first student group"}
          </h3>
          <p className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
            Group students by classroom or cohort, then assign quizzes, homework, or flashcard reviews and track completion in one place.
          </p>
          {!q && (
            <button
              onClick={() => setEditing("new")}
              data-testid="groups-create-first"
              className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-white font-medium px-4 py-2.5 rounded-xl transition-all"
            >
              <Plus className="w-4 h-4" /> New group
            </button>
          )}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="groups-grid">
          {filtered.map((g) => (
            <div
              key={g.group_id}
              data-testid={`group-card-${g.group_id}`}
              className="bg-white border border-border rounded-2xl p-5 flex flex-col gap-4 hover:border-sage/50 transition-all"
            >
              <div>
                <div className="font-display text-lg font-semibold leading-tight">{g.name}</div>
                {g.description && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{g.description}</p>
                )}
              </div>
              <div className="flex items-center gap-4 text-xs">
                <div className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <Users className="w-3.5 h-3.5 text-sage" /> {g.student_count} students
                </div>
                <div className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <ClipboardList className="w-3.5 h-3.5 text-terracotta" /> {g.assignment_count || 0} assigned
                </div>
              </div>
              <div className="mt-auto flex items-center gap-1.5 flex-wrap">
                <button
                  onClick={() => setSelectedGroupId(g.group_id)}
                  data-testid={`group-open-${g.group_id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sage/10 hover:bg-sage/20 text-sage text-xs font-medium transition"
                >
                  <BarChart3 className="w-3.5 h-3.5" /> Open
                </button>
                <button
                  onClick={() => setEditing(g)}
                  data-testid={`group-edit-${g.group_id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-border hover:bg-secondary text-xs font-medium transition"
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
                <button
                  onClick={() => handleDelete(g)}
                  data-testid={`group-delete-${g.group_id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-border hover:bg-terracotta/10 hover:text-terracotta text-xs font-medium transition ms-auto"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <GroupFormModal
          initial={editing === "new" ? null : editing}
          roster={roster}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {selectedGroupId && (
        <GroupDetailDrawer
          groupId={selectedGroupId}
          onClose={() => setSelectedGroupId(null)}
          onChanged={load}
        />
      )}

      {/* Tiny legend for teachers who clicked into an empty state */}
      {!loading && filtered.length > 0 && (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5 text-sage" />
          Open any group to manage members, create assignments, and view the leaderboard.
        </div>
      )}
    </div>
  );
}
